import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Link, Popover, PopoverContent, Status, useBasicPopover } from '@kc-console/common-ui';
import { IcChevronDownSmallRegular, IcChevronUpSmallRegular } from '@kep/kcds-icons';

import { getServiceStatusVariant } from '../model/getServiceStatusVariant';

import type { ServiceStatusItem } from '../model/types';

import { ROUTE_PATH } from '@/shared/config/routes';
import { useRegionEndpoint } from '@/shared/hooks';

interface ServiceStatusCellProps {
  item?: ServiceStatusItem;
}

/**
 * 서비스 상태 테이블의 상태 셀.
 * @description
 * - 상태 인디케이터를 표시하고, 이벤트가 있는 경우 꺽새 버튼 클릭 시 Popover로 이벤트 목록을 보여준다.
 * - 상태 아이콘은 클릭 불가, 꺽새 버튼만 클릭 가능하다.
 * - 이벤트 링크는 새 탭으로 열린다.
 * @example
 * <ServiceStatusCell item={statusItem} />
 */
const ServiceStatusCell = ({ item }: ServiceStatusCellProps) => {
  const { currentRegion } = useRegionEndpoint();
  const { anchorEl, open, id, onClick, onClose } = useBasicPopover<HTMLButtonElement>();

  if (!item) {
    return <CellRoot />;
  }

  const { status, eventList = [] } = item;
  const variant = getServiceStatusVariant(status);
  const hasEvents = eventList.length > 0;

  return (
    <CellRoot>
      <Status status={variant} hideLabel />
      <ChevronButton
        aria-describedby={hasEvents ? id : undefined}
        aria-expanded={hasEvents ? open : undefined}
        type="button"
        active={open}
        visible={hasEvents}
        onClick={hasEvents ? onClick : undefined}
      >
        {open ? <ChevronUpIcon fontSize={16} /> : <ChevronDownIcon fontSize={16} />}
      </ChevronButton>
      {hasEvents && (
        <Popover id={id} open={open} anchorEl={anchorEl} onClose={onClose} placement="bottom">
          <PopoverContent arrow placement="bottom">
            <PopoverList>
              {eventList.map(eventItem => (
                <li key={eventItem.eventId}>
                  <Link
                    href={`${ROUTE_PATH.EVENT_LIST}/${eventItem.eventId}/info?region=${currentRegion}`}
                    size="xs"
                    external
                    onClick={onClose}
                  >
                    {eventItem.eventTitle}
                  </Link>
                </li>
              ))}
            </PopoverList>
          </PopoverContent>
        </Popover>
      )}
    </CellRoot>
  );
};

export default ServiceStatusCell;

const CellRoot = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
`;

const ChevronButton = styled('button', {
  shouldForwardProp: prop => prop !== 'active' && prop !== 'visible',
})<{ active: boolean; visible: boolean }>(
  ({ theme, active, visible }) => css`
    display: flex;
    height: 32px;
    min-height: 32px;
    max-height: 32px;
    padding: 0 8px;
    justify-content: center;
    align-items: center;
    border-radius: 1px;
    border: none;
    background: ${active ? theme.color['actionSurface/ghost/press'] : 'transparent'};
    cursor: ${visible ? 'pointer' : 'default'};
    visibility: ${visible ? 'visible' : 'hidden'};
    outline: none;

    &:hover {
      background: ${visible && !active ? theme.color['actionSurface/ghost/hover'] : undefined};
    }
  `,
);

const ChevronDownIcon = styled(IcChevronDownSmallRegular)(
  ({ theme }) => css`
    color: ${theme.color['icon/weak/default']};
  `,
);

const ChevronUpIcon = styled(IcChevronUpSmallRegular)(
  ({ theme }) => css`
    color: ${theme.color['icon/weak/default']};
  `,
);

const PopoverList = styled('ul')`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
`;
